import React from 'react';
import DropZone from './DropZone';
import UploadProgress from './UploadProgress';
import UploadConfirmDialog from './UploadConfirmDialog';
import { useDicomUpload } from '../../hooks/useDicomUpload';

interface DicomUploadProps {
  /**
   * Passed by useWorkListToolbarActions.tsx:35 and deliberately unused: uploads go
   * to the VIEWER API ({viewerRoot}/dicom-web/studies), not through the data
   * source's STOW-RS store. D10 records the consequence — a 200 from the viewer API
   * does not prove the PACS has indexed the study.
   */
  dataSource?: unknown;
  onComplete: () => void;
  onStarted: () => void;
}

export default function DicomUpload({ onComplete, onStarted }: DicomUploadProps) {
  const {
    phase,
    rows,
    activeIndex,
    haltMessage,
    canForce,
    isResolving,
    stats,
    handleFiles,
    continueWithForce,
    stopAndRollback,
    cancelAll,
  } = useDicomUpload({
    onComplete,
    onStarted,
  });

  if (phase === 'idle' || phase === 'intaking') {
    return (
      <div className="bg-background flex h-[480px] min-h-[480px] flex-col select-none rounded-lg">
        <DropZone
          isIntaking={phase === 'intaking'}
          onFiles={handleFiles}
        />
      </div>
    );
  }

  return (
    <div
      className="bg-background flex h-[calc(100vh-300px)] min-h-[480px] flex-col select-none rounded-lg"
      data-cy="sycai-upload-progress"
    >
      <UploadProgress
        rows={rows}
        activeIndex={activeIndex}
        stats={stats}
        isUploading={phase === 'uploading'}
        onCancelAll={cancelAll}
      />

      <UploadConfirmDialog
        open={phase === 'halted'}
        errorMessage={haltMessage ?? 'Unknown error occurred during the upload.'}
        canForce={canForce}
        isResolving={isResolving}
        onContinue={continueWithForce}
        onStop={stopAndRollback}
      />
    </div>
  );
}
